import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// Files a clinician attaches to a visit (labs, investigations, previous
// notes). Without fast attach the file is read by a model once at upload and
// only the extracted text and its labels are stored. With fast attach
// (VISIT_SOURCE_FAST_ATTACH_ENABLED) nothing is read by a model at upload:
// PDF text layers and Word documents are extracted locally and de-identified;
// photos, screenshots and scanned PDFs are kept as files and read by the model
// only when the assistant or a new note uses them (`readOnUse`).
// Source identities are outside the eight covered record resources; only the
// parent visit selector and relationship use public UUIDs.
const id = z.int().positive().max(4294967295);
const pathId = z
  .string()
  .regex(/^[1-9][0-9]*$/)
  .transform(Number)
  .pipe(id);

export const VISIT_SOURCE_MAX_BYTES = 10485760;
export const VISIT_SOURCE_MAX_PER_VISIT = 10;
export const VISIT_SOURCE_TEXT_MAX = 20000;
export const VISIT_SOURCE_DOCX_MEDIA_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const VISIT_SOURCE_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  // Fast attach only.
  VISIT_SOURCE_DOCX_MEDIA_TYPE,
] as const;
// The media types every client knows. Clients that decode a closed list
// (iOS builds before Word support) get a Word source as "application/pdf".
export const VISIT_SOURCE_LEGACY_MEDIA_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;
// Prior record: history from before this visit (earlier results, letters,
// notes). Today: produced during this encounter (e.g. point-of-care tests).
export const visitSourceTimingSchema = z.enum(["prior", "today"]);
export const visitSourceKindSchema = z.enum([
  "labs",
  "investigation",
  "previous-note",
  "other",
]);
const checksum = z
  .string()
  .length(44)
  .refine((value) => {
    try {
      return atob(value).length === 32 && btoa(atob(value)) === value;
    } catch {
      return false;
    }
  }, "Expected canonical SHA-256 base64");
export const visitSourceCreateSchema = z.strictObject({
  timing: visitSourceTimingSchema,
  mediaType: z.enum(VISIT_SOURCE_MEDIA_TYPES),
  // Base64 file content, at most VISIT_SOURCE_MAX_BYTES once decoded.
  data: z
    .string()
    .min(4)
    .max(Math.ceil(VISIT_SOURCE_MAX_BYTES / 3) * 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Expected base64"),
  checksumSha256: checksum,
  // The clinician confirmed the file has no patient identifiers.
  identifiersAcknowledged: z.literal(true),
});
export type VisitSourceCreateInput = z.output<typeof visitSourceCreateSchema>;
export const visitSourceSchema = z.object({
  id,
  visitPublicId: publicIdSchema,
  timing: visitSourceTimingSchema,
  kind: visitSourceKindSchema,
  title: z.string().min(1).max(160),
  documentDate: z.iso.date().nullable(),
  mediaType: z.enum(VISIT_SOURCE_MEDIA_TYPES),
  byteLength: z.int().positive().max(VISIT_SOURCE_MAX_BYTES),
  // Extracted, de-identified text. Empty for a file that is read on use
  // until the assistant has pulled something out of it.
  text: z.string().max(VISIT_SOURCE_TEXT_MAX),
  // Identifiers were replaced (names, dates of birth...).
  identifiersRedacted: z.boolean(),
  createdAt: z.iso.datetime(),
  // Fast attach: the file is kept and read by the model when it is used.
  // Only sent when true, so older clients never see the key otherwise.
  readOnUse: z.literal(true).optional(),
});
export type VisitSource = z.output<typeof visitSourceSchema>;
// Selected sources for note generation or an assistant proposal.
export const visitSourceIdsSchema = z
  .array(id)
  .max(VISIT_SOURCE_MAX_PER_VISIT)
  .refine((ids) => new Set(ids).size === ids.length, "Duplicate source")
  .transform((ids) => ids.toSorted((a, b) => a - b));

const sources = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  NOT_FOUND: { message: "Visit source not found" },
  CONFLICT: { message: "Visit source conflict" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  SERVICE_UNAVAILABLE: { message: "Visit sources unavailable" },
});
const query = z.strictObject({}).default({});
const meta = (
  method: "GET" | "POST" | "DELETE",
  path: string,
  successStatus = 200,
) =>
  openapi({
    method,
    path: `/api/v2${path}`,
    inputStructure: "detailed",
    successStatus,
    spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
  });
export const visitSourceContract = {
  listVisitSources: sources
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query,
      }),
    )
    .meta(meta("GET", "/visits/{public_id}/sources"))
    .output(
      z.object({
        sources: z.array(visitSourceSchema).max(VISIT_SOURCE_MAX_PER_VISIT),
      }),
    ),
  createVisitSource: sources
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query,
        headers: z.object({ "idempotency-key": z.uuid().toLowerCase() }),
        body: visitSourceCreateSchema,
      }),
    )
    .meta(meta("POST", "/visits/{public_id}/sources", 201))
    .output(visitSourceSchema),
  deleteVisitSource: sources
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema, sourceId: pathId }),
        query,
        body: z.undefined().optional(),
      }),
    )
    .meta(meta("DELETE", "/visits/{public_id}/sources/{sourceId}"))
    .output(z.object({ id })),
};
